import { policy } from '../../data/policies'
import { accountShape, lastVisit, refillState, upcomingAppointments } from '../../utils/account'
import { daysSince, newestFirst } from '../../core/format/dates'
import type { Account } from '../../core/types/account'
import type { AttentionFlag, CustomerSummary } from '../../core/types/adminRequests'

/**
 * Klantdossiers in het beheer. Puur, zonder React. De backend berekent de
 * samenvatting straks zelf (GET /admin/customers); de demo gebruikt deze
 * functies, zodat de regels op één plek staan.
 */

type Named = Pick<Account['customer'], 'firstName' | 'lastName'>

export const customerName = (a: Named) => [a.firstName, a.lastName].filter(Boolean).join(' ') || 'Naamloos'

export const initials = (a: Named) => `${a.firstName.charAt(0)}${a.lastName.charAt(0)}`.toUpperCase() || '?'

export type CustomerTab = 'overzicht' | 'huidplan' | 'afspraken' | 'abonnement' | 'bestellingen' | 'fotos'

/**
 * Waar Sarah iets mee moet, op volgorde van belang. `revisitAfterWeeks`: het beheer geeft het
 * concept mee (Instellingen); de demo-server gebruikt de opgeslagen instelling.
 */
export function attentionFor(account: Account, revisitAfterWeeks = policy.revisitAfterWeeks): AttentionFlag[] {
  const flags: AttentionFlag[] = []
  const upcoming = upcomingAppointments(account.appointments)
  const shape = accountShape(account)
  const visit = lastVisit(account)

  if (account.appointments.some((a) => a.status === 'aanvraag')) flags.push('aanvraag')
  if (account.plan.items.length === 0 && account.orders.length > 0) flags.push('geen-plan')
  if (account.course && account.course.done >= account.course.total && upcoming.length === 0) flags.push('kuur-klaar')
  if (shape.salon && upcoming.length === 0 && visit && daysSince(visit.date) > revisitAfterWeeks * 7) flags.push('terugzien')
  if (account.plan.items.some((i) => refillState(i).level !== 'ruim')) flags.push('bijna-op')
  return flags
}

export const attentionLabel: Record<AttentionFlag, string> = {
  aanvraag: 'Afspraak bevestigen',
  'geen-plan': 'Nog geen huidplan',
  'kuur-klaar': 'Kuur afgerond',
  terugzien: 'Lang niet gezien',
  'bijna-op': 'Product bijna op',
}

/** Naar welk tabblad van het dossier een melding leidt. */
export const attentionTab: Record<AttentionFlag, CustomerTab> = {
  aanvraag: 'afspraken',
  'geen-plan': 'huidplan',
  'kuur-klaar': 'afspraken',
  terugzien: 'afspraken',
  'bijna-op': 'huidplan',
}

export function summarize(account: Account): CustomerSummary {
  const c = account.customer
  const next = upcomingAppointments(account.appointments)[0]
  const orders = newestFirst(account.orders)
  return {
    id: c.id,
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email,
    phone: c.phone,
    city: c.address.city,
    memberSince: c.memberSince,
    shape: accountShape(account),
    subscription: account.subscription?.status ?? null,
    nextAppointment: next ? { date: next.date, time: next.time, treatmentSlug: next.treatmentSlug, status: next.status } : null,
    lastVisit: lastVisit(account)?.date ?? null,
    lastOrder: orders[0]?.date ?? null,
    hasPlan: account.plan.items.length > 0,
    course: account.course,
    attention: attentionFor(account),
    acquisitionChannel: c.acquisitionSource?.channel ?? null,
  }
}

/** Hoe je haar bereikt, voor onder de naam in een lijst: e-mail, anders telefoon (klant zonder e-mail). */
export const contactHint = (c: { email: string; phone?: string }) => c.email || c.phone || ''

/** Waar het zoekveld van het beheer in kijkt: naam, e-mail en telefoon (ook "0612…" tegen "06 12…"). */
export const customerSearchText = (c: { firstName: string; lastName: string; email: string; phone?: string; city?: string }) =>
  `${c.firstName} ${c.lastName} ${c.email} ${c.city ?? ''} ${c.phone ?? ''} ${(c.phone ?? '').replace(/\D/g, '')}`.toLowerCase()

/** "Thuis & salon", "Alleen salon", "Alleen webshop", "Nog niets". */
export const shapeLabel = ({ home, salon }: CustomerSummary['shape']) =>
  home && salon ? 'Thuis & salon' : salon ? 'Alleen salon' : home ? 'Alleen webshop' : 'Nog niets afgenomen'

/** "06 12 34 56 78" → "31612345678" voor een wa.me-link. Leeg als het geen mobiel nummer lijkt. */
export const whatsappNumber = (phone: string) => {
  const digits = phone.replace(/\D/g, '')
  if (digits.startsWith('06')) return `31${digits.slice(1)}`
  if (digits.startsWith('316')) return digits
  return ''
}
